/** @jsxImportSource @emotion/react */
import type React from 'react';
import { useState } from 'react';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import {
  ButtonsCol,
  Card,
  Disclaimer,
  ErrorText,
  FieldInput,
  FieldLabel,
  FieldRow,
  FirstParagraph,
  PrimaryButton,
  Screen,
  SecondaryButton,
  SecondParagraph,
  SpinnerWrap,
  Link,
  TopPart,
  Form,
} from './waitlist.styled';
import { VSpace } from '../../chat/settings/account/account-settings';
import type { LoginStepProps } from '../login-shell.types';
import { waitlistApi, type WaitlistRequest } from '../../../api';

export const Waitlist: React.FC<LoginStepProps> = ({ go }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    nickname: '',
    company: '',
  });

  const [formErrors, setFormErrors] = useState({
    email: '',
    firstName: '',
    lastName: '',
    nickname: '',
    company: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateInput = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const sqlInjectionPattern = /(--|;|'|"|\/\*|\*\/|xp_)/i;

    const { email, firstName, lastName, company, nickname } = formData;
    const errors = {
      email: '',
      firstName: '',
      lastName: '',
      nickname: '',
      company: '',
    };

    if (!emailRegex.test(email)) errors.email = 'Please enter a valid email address.';
    if (firstName.trim().length < 2 || firstName.trim().length > 100)
      errors.firstName = 'First name must be between 2 and 100 characters.';
    if (lastName.trim().length < 2 || lastName.trim().length > 100)
      errors.lastName = 'Last name must be between 2 and 100 characters.';
    if (company.trim().length < 2 || company.trim().length > 100)
      errors.company = 'Company name must be between 2 and 100 characters.';
    if (nickname.trim().length > 0 && (nickname.trim().length < 2 || nickname.trim().length > 100))
      errors.nickname = 'Nickname must be between 2 and 100 characters.';

    if (sqlInjectionPattern.test(email))
      errors.email = errors.email || 'Invalid characters detected in email.';
    if (sqlInjectionPattern.test(firstName))
      errors.firstName = errors.firstName || 'Invalid characters detected in first name.';
    if (sqlInjectionPattern.test(lastName))
      errors.lastName = errors.lastName || 'Invalid characters detected in last name.';
    if (sqlInjectionPattern.test(company))
      errors.company = errors.company || 'Invalid characters detected in company name.';
    if (sqlInjectionPattern.test(nickname))
      errors.nickname = errors.nickname || 'Invalid characters detected in nickname.';

    setFormErrors(errors);

    return (
      !errors.email &&
      !errors.firstName &&
      !errors.lastName &&
      !errors.nickname &&
      !errors.company
    );
  };

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateInput()) return;

    setLoading(true);
    setError(null);

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const payload: WaitlistRequest = {
      email: formData.email.trim(),
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      nickname: formData.nickname.trim() || null,
      company: formData.company.trim(),
      timezone,
      turnstile_token: 'dev-bypass', // For now, since this is the app version
    };

    try {
      await waitlistApi.join(payload);
      go('waitlist-sent');
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409) {
        setError(detail || 'This email is already on the waitlist.');
      } else {
        console.error('Failed to submit waitlist:', err);
        setError(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen data-testid="waitlist-screen">
      <TopPart>
        <FirstParagraph>
        Request an invitation to Mailander
        </FirstParagraph>
        <SecondParagraph>
        Private access is limited. Join the waitlist.
        </SecondParagraph>
      </TopPart>
      <Card>
        <Form style={{ width: "100%" }} onSubmit={handleSubmit}>
          <FieldRow>
            <FieldLabel>
              Email
            </FieldLabel>
            <FieldInput
              name='email'
              type='email'
              value={formData.email}
              onChange={handleInputChange('email')}
              data-testid="waitlist-email"
            />
            {formErrors.email && <ErrorText>{formErrors.email}</ErrorText>}
          </FieldRow>
          <FieldRow>
            <FieldLabel>
              First name
            </FieldLabel>
            <FieldInput
              name='firstName'
              type='text'
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              data-testid="waitlist-first-name"
            />
            {formErrors.firstName && <ErrorText>{formErrors.firstName}</ErrorText>}
          </FieldRow>
          <FieldRow>
            <FieldLabel>
              Last name
            </FieldLabel>
            <FieldInput
              name='lastName'
              type='text'
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              data-testid="waitlist-last-name"
            />
            {formErrors.lastName && <ErrorText>{formErrors.lastName}</ErrorText>}
          </FieldRow>
          <FieldRow>
            <FieldLabel>
              Nickname
            </FieldLabel>
            <FieldInput
              name='nickname'
              type='text'
              value={formData.nickname}
              onChange={handleInputChange('nickname')}
              data-testid="waitlist-nickname"
            />
            {formErrors.nickname && <ErrorText>{formErrors.nickname}</ErrorText>}
          </FieldRow>
          <FieldRow>
            <FieldLabel>
              Company
            </FieldLabel>
            <FieldInput
              name='company'
              type='text'
              value={formData.company}
              onChange={handleInputChange('company')}
              data-testid="waitlist-company"
            />
            {formErrors.company && <ErrorText>{formErrors.company}</ErrorText>}
          </FieldRow>
          <VSpace $h={12} />

          <ButtonsCol>
            <PrimaryButton type="submit" disabled={loading} data-testid="waitlist-submit">
              Submit request
            </PrimaryButton>

            {loading && (
              <SpinnerWrap data-testid="waitlist-loading">
                <UniversalSpinner />
              </SpinnerWrap>
            )}

            {!loading && error && (
              <>
                <VSpace $h={12} />
                <ErrorText data-testid="waitlist-error">{error}</ErrorText>
              </>
            )}
          </ButtonsCol>
        </Form>
        <Disclaimer>
        By submitting, you agree to our <Link href="https://mailander.com/terms-of-use/">Terms of Use</Link> and <Link href="https://mailander.com/privacy-policy/">Privacy Policy</Link>.
        </Disclaimer>
      </Card>
      <VSpace $h={24} />
      <SecondaryButton onClick={() => go('email-login')} disabled={loading} data-testid="waitlist-email-login">
        Go back
      </SecondaryButton>
    </Screen>
  );
};
